import React, { useEffect, useState } from "react";
import { Upload, CheckCircle, FileText } from "lucide-react";
import { getUserProfile } from "./services/api";

const StartScreen = ({
  resumeData,
  setResumeData,
  userEmail,
  handleResumeUpload,
  handleParseResume,
  loadingProgress,
  loadingMessage,
  isLoading,
  navigateTo,
  navigateBack,
}) => {
  const [hasResumeInDatabase, setHasResumeInDatabase] = React.useState(false);
  const [resumeUploaded, setResumeUploaded] = useState(false);
  useEffect(() => {
    // Check if resume exists in database
    const handleGetCurrentResume = async () => {
      const resumeInfo = await getUserProfile(
        userEmail,
        false,
        true,
        false,
        false,
      );
      setResumeData(resumeInfo.resumeData || {});
    };
    handleGetCurrentResume();
    setHasResumeInDatabase(resumeData.length <= 0);
  }, [userEmail]);

  return (
    <div className="min-h-screen bg-gradient-to-br flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
        <div className="text-center mb-2">
          <div className="text-left">
            <h2 className="applicant-theme-text">Resume Status</h2>

            {hasResumeInDatabase ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-green-800 font-medium">
                    Resume found in database
                  </span>
                </div>
                {resumeData.resume?.filename && (
                  <p className="text-sm text-green-600 mb-1">
                    File: {resumeData.resume?.filename}
                  </p>
                )}
                {resumeData.resume?.uploaded_at && (
                  <p className="text-xs text-green-500 mb-3">
                    Parsed:{" "}
                    {new Date(
                      resumeData.resume?.uploaded_at,
                    ).toLocaleDateString()}
                  </p>
                )}
                <div className="text-sm text-green-700">
                  <p>
                    • Work Experience: {resumeData.work_experience?.length || 0}{" "}
                    positions
                  </p>
                  <p>
                    • Skills: {Object.keys(resumeData.skills || {}).length}{" "}
                    categories
                  </p>
                  <p>• Projects: {resumeData.projects?.length || 0}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 text-sm font-semibold mb-4">
                Upload and parse your resume to get started
              </p>
            )}
          </div>
        </div>

        {hasResumeInDatabase ? (
          // Show options when resume exists
          <div className="relative">
            <div className="buttons-container">
              <button
                onClick={() => navigateTo("personalInfo")}
                className="buttons-indigo"
              >
                Continue with Current Resume
              </button>

              <div className="text-center">
                <p className="text-sm text-indigo-600 mb-3">
                  Or upload a new resume:
                </p>
              </div>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
              <p className="text-gray-600 mb-2">Upload new resume</p>
              <p className="text-xs text-gray-500 mb-3">
                This will replace your current resume
              </p>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  setResumeUploaded(true);
                  handleResumeUpload(e);
                }}
                id="resume-upload"
                style={{ display: "none" }}
              />
              <label
                htmlFor="resume-upload"
                className="inline-block text-indigo-600 px-4 py-2 rounded-lg cursor-pointer transition-colors font-medium text-sm"
              >
                Choose New File
              </label>
            </div>
            {!(loadingProgress in ["", 0]) && isLoading && (
              <div className="absolute bg-loading-opacity-50 top-0 z-2 w-full h-full overlaying-container rounded-lg bg-loading-color">
                <img
                  src="/assets/Applicant-parsing.png"
                  className="w-25 h-25 opacity-75 max-auto"
                  alt="Applicant parsing loading logo"
                />
                <span className="text-center text-indigo-600 font-medium">
                  {loadingMessage} - {loadingProgress}%
                </span>
              </div>
            )}
          </div>
        ) : (
          // Show upload interface when no resume
          <div className="relative">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
              <Upload className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
              <p className="text-gray-600 mb-2">Click to browse</p>
              <p className="text-xs text-gray-500 mb-4">
                Supported formats: PDF, DOC, DOCX
              </p>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  setResumeUploaded(true);
                  handleResumeUpload(e);
                }}
                id="resume-upload"
                style={{ display: "none" }}
              />
              <label
                htmlFor="resume-upload"
                className="inline-block text-indigo-600 px-6 py-3 rounded-lg cursor-pointer transition-colors font-medium"
              >
                Upload Resume
              </label>
            </div>
            {!(loadingProgress in ["", 0]) && isLoading && (
              <div className="absolute bg-loading-opacity-50 top-0 z-2 w-full h-full overlaying-container rounded-lg bg-loading-color">
                <img
                  src="/assets/Applicant-parsing.png"
                  className="w-25 h-25 opacity-75 max-auto"
                  alt="Applicant parsing loading logo"
                />
                <span className="text-center text-indigo-600 font-medium">
                  {loadingMessage} - {loadingProgress}%
                </span>
              </div>
            )}
          </div>
        )}

        {resumeData.resume && (
          <div className="bg-indigo-50 border rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-indigo-600 mr-2" />
                <span className="text-indigo-600 font-medium text-wrap text-center">
                  {resumeData.resume?.name}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="buttons-container">
          {resumeUploaded && !hasResumeInDatabase && (
            <button
              onClick={handleParseResume}
              disabled={!resumeData.resume || isLoading}
              className="buttons-green"
            >
              {isLoading ? "Analyzing Resume..." : "Parse & Continue"}
            </button>
          )}
        </div>
        {isLoading && (
          <p className="text-xs text-center">
            Parsing might take a minute. Please wait.
          </p>
        )}
      </div>
    </div>
  );
};

export default StartScreen;
